import useGetNavigation from "@RHCommerceDev/hooks/useGetNavigation";
import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import useUserPreferences from "@RHCommerceDev/hooks/useUserPreferences";
import {
  countries,
  countryAbbreviationMapper
} from "@RHCommerceDev/resources/countries-config.json";
import { cn } from "@RHCommerceDev/utils/cn";
import parsedCatalogNavigation, {
  groupChildCategories
} from "@RHCommerceDev/utils/parseCatalogNavigationData";
import { usePageContent } from "customProviders/LocationProvider";
import React, { Dispatch, SetStateAction, useCallback, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { Typography } from "unstyled-ui-components";
import HamburgerCountrySelector from "./hamburgerCountrySelector";
import memoize from "@RHCommerceDev/utils/memoize";
import parseUrlPath from "@RHCommerceDev/utils/parse-url-path";
import { getGlobal } from "@RHCommerceDev/utils-globals/getGlobal";

const env = getGlobal("_env");

const getLocalizedUrl = (targetUrl: string, locale: string): string => {
  const path = targetUrl.startsWith("/") ? `${locale}${targetUrl}` : targetUrl;

  return env.FEATURE_SHOP_UI ? parseUrlPath(path) : path;
};

const PrimaryColumn = ({
  sections,
  selectedItem,
  handlePrimaryClick,
  locale,
  selectedCountry,
  selectedLanguage,
  isCountrySelectorOpen,
  setIsCountrySelectorOpen
}: {
  sections: (NavigationElement | null | undefined)[];
  selectedItem: {
    sectionIndex: number;
    primaryIndex: number;
    secondaryIndex: number;
  };
  handlePrimaryClick: (sectionIndex: number, primaryIndex: number) => void;
  locale: string;
  selectedCountry: string;
  selectedLanguage: string;
  isCountrySelectorOpen: boolean;
  setIsCountrySelectorOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  return (
    <div className="w-3xs flex min-h-screen min-w-80 flex-col items-start gap-12 overflow-auto border-r-2 border-r-white/25">
      {sections?.map((section, sectionIndex) => (
        <div key={sectionIndex} className="flex flex-col items-start gap-5">
          <Typography
            variant="caption"
            fontFamily="rhc"
            className="text-base font-semibold uppercase leading-[1.5em] tracking-[0.05em]"
          >
            {section?.displayName ?? ""}
          </Typography>

          {section?.childCategories?.map((primaryItem, primaryIndex) => {
            return Array.isArray(primaryItem?.childCategories) ? (
              <button
                key={primaryIndex}
                onClick={() => {
                  setIsCountrySelectorOpen(false);
                  handlePrimaryClick(sectionIndex, primaryIndex);
                }}
                className="p-0 text-white"
              >
                <Typography
                  variant="caption"
                  fontFamily="rhc"
                  fontWeight="thin"
                  className={cn(
                    "text-base uppercase leading-[1.5em] tracking-[0.05em] text-inherit",
                    {
                      "font-semibold":
                        selectedItem?.primaryIndex === primaryIndex &&
                        selectedItem?.sectionIndex === sectionIndex
                    }
                  )}
                >
                  {primaryItem?.displayName}
                </Typography>
              </button>
            ) : (
              <a
                key={primaryIndex}
                href={getLocalizedUrl(primaryItem?.targetUrl ?? "", locale)}
              >
                <Typography
                  variant="caption"
                  fontFamily="rhc"
                  fontWeight="thin"
                  className={cn(
                    "text-base uppercase leading-[1.5em] tracking-[0.05em] text-white",
                    {
                      "font-semibold":
                        selectedItem?.primaryIndex === primaryIndex &&
                        selectedItem?.sectionIndex === sectionIndex
                    }
                  )}
                >
                  {primaryItem?.displayName}
                </Typography>
              </a>
            );
          })}

          {sectionIndex === 0 && (
            <button
              onClick={() => {
                setIsCountrySelectorOpen(prev => !prev);
                handlePrimaryClick(-1, -1);
              }}
              className="relative mt-7 flex w-full items-center p-0 py-4 uppercase text-white before:absolute before:top-0 before:h-px before:w-full before:bg-white before:opacity-25 before:content-[''] after:absolute after:bottom-0 after:h-px after:w-full after:bg-white after:opacity-25 after:content-['']"
            >
              <Typography
                variant="caption"
                fontFamily="rhc"
                fontWeight="thin"
                className={cn(
                  "text-base uppercase leading-[3em] tracking-[0.05em] text-inherit ",
                  {
                    "font-semibold": isCountrySelectorOpen
                  }
                )}
              >
                {
                  countryAbbreviationMapper[
                    selectedCountry === "MC"
                      ? "FR"
                      : (selectedCountry as keyof typeof countryAbbreviationMapper)
                  ]
                }
                {countries[
                  selectedCountry === "MC"
                    ? "FR"
                    : (selectedCountry as keyof typeof countries)
                ].label?.match(/^(.+?)\s*(\([^)]+\))?$/)?.[2] ?? ""}{" "}
                {selectedLanguage}
              </Typography>
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

const SecondaryColumn = ({
  items,
  selectedItem,
  handleSecondaryClick,
  locale,
  isCountrySelectorOpen
}: {
  items: (NavigationElement | null | undefined)[] | undefined;
  selectedItem: {
    sectionIndex: number;
    primaryIndex: number;
    secondaryIndex: number;
  };
  handleSecondaryClick: (index: number) => void;
  locale: string;
  isCountrySelectorOpen: boolean;
}) => (
  <div className="mt-10 flex flex-col items-start gap-5 overflow-auto pr-3 text-white">
    {isCountrySelectorOpen ? (
      <HamburgerCountrySelector />
    ) : (
      <>
        {items?.map((secondaryItem, secondaryIndex) =>
          Array.isArray(secondaryItem?.childCategories) ? (
            <button
              key={secondaryIndex}
              onClick={() => handleSecondaryClick(secondaryIndex)}
              className="p-0 text-white"
            >
              <Typography
                variant="caption"
                fontFamily="rhc"
                fontWeight="thin"
                className={cn(
                  "text-base uppercase leading-[1.5em] tracking-[0.05em] text-inherit",
                  {
                    "font-semibold":
                      selectedItem.secondaryIndex === secondaryIndex
                  }
                )}
              >
                {secondaryItem?.displayName}
              </Typography>
            </button>
          ) : (
            <a
              key={secondaryIndex}
              href={getLocalizedUrl(secondaryItem?.targetUrl ?? "", locale)}
            >
              <Typography
                variant="caption"
                fontFamily="rhc"
                fontWeight="thin"
                className={cn(
                  "text-base uppercase leading-[1.5em] tracking-[0.05em] text-white",
                  {
                    "font-semibold":
                      selectedItem.secondaryIndex === secondaryIndex
                  }
                )}
              >
                {secondaryItem?.displayName}
              </Typography>
            </a>
          )
        )}
      </>
    )}
  </div>
);

const TertiaryColumn = ({
  groups,
  locale
}: {
  groups: ReturnType<typeof groupChildCategories>;
  locale: string;
}) => (
  <div className="mt-10 columns-2 gap-x-10 space-y-5 text-white [column-fill:_auto]">
    {groups?.map((group, groundIndex) => (
      <div
        key={groundIndex}
        className="flex break-inside-avoid flex-col gap-2.5"
      >
        {group?.header && (
          <Typography
            variant="caption"
            fontFamily="rhc"
            className="text-base font-semibold uppercase leading-[1.5em] tracking-[0.05em] text-white"
          >
            {group.header?.displayName}
          </Typography>
        )}

        {group?.children?.map((tertiaryItem, tertiaryIndex) => (
          <a
            key={tertiaryIndex}
            href={getLocalizedUrl(tertiaryItem?.targetUrl ?? "", locale)}
          >
            <Typography
              variant="caption"
              fontFamily="rhc"
              fontWeight="thin"
              className="text-base capitalize leading-[1.5em] tracking-[0.05em] text-white"
            >
              {tertiaryItem?.displayName}
            </Typography>
          </a>
        ))}
      </div>
    ))}
  </div>
);

const WORHMenuContentV2 = () => {
  const locale = useLocalization();
  const { pageContent: pageContentObj } = usePageContent();
  const hamburgerNav = pageContentObj?.hamburgerNav as Record<string, unknown>;
  const pageContent = hamburgerNav?.childCategories
    ? Array.isArray(hamburgerNav.childCategories)
      ? hamburgerNav.childCategories
      : Object.values(hamburgerNav.childCategories)
    : [];

  const isMobileScreen = isMobile;
  const isTabletScreen = isTablet;
  const isSmallDevice = isMobileScreen || isTabletScreen;

  const { catalogNavigation, saleCatalogNavigation } = useGetNavigation();
  const navigationData = parsedCatalogNavigation(
    catalogNavigation,
    saleCatalogNavigation
  );

  const { country, language } = useUserPreferences();

  const sections = [navigationData];
  sections.push(...pageContent);

  const [selectedItem, setSelectedItem] = useState<{
    sectionIndex: number;
    primaryIndex: number;
    secondaryIndex: number;
  }>({
    sectionIndex: -1,
    primaryIndex: -1,
    secondaryIndex: -1
  });

  const [isCountrySelectorOpen, setIsCountrySelectorOpen] =
    useState<boolean>(false);

  const selectedPrimary =
    sections?.[selectedItem.sectionIndex]?.childCategories?.[
      selectedItem.primaryIndex
    ];
  const selectedSecondary =
    selectedPrimary?.childCategories?.[selectedItem.secondaryIndex];

  const groupedCategories = groupChildCategories(
    selectedSecondary?.childCategories
  );

  const handlePrimaryClick = useCallback(
    (sectionIndex: number, primaryIndex: number) => {
      setSelectedItem({ sectionIndex, primaryIndex, secondaryIndex: -1 });
    },
    []
  );

  const handleSecondaryClick = useCallback((secondaryIndex: number) => {
    setSelectedItem(prev => ({ ...prev, secondaryIndex }));
  }, []);

  if (isSmallDevice) return null;

  return (
    <div className="z-[1200] flex h-screen gap-16 bg-rh-mocha pl-10 pr-10 xl:pl-20 xl:pr-20">
      {/* PRIMARY COLUMN */}
      <PrimaryColumn
        sections={sections}
        selectedItem={selectedItem}
        handlePrimaryClick={handlePrimaryClick}
        locale={locale}
        selectedCountry={country}
        selectedLanguage={language.split("-")[0].toUpperCase()}
        isCountrySelectorOpen={isCountrySelectorOpen}
        setIsCountrySelectorOpen={setIsCountrySelectorOpen}
      />

      {/* SECONDARY COLUMN */}
      {(selectedItem.primaryIndex !== -1 || isCountrySelectorOpen) && (
        <SecondaryColumn
          items={selectedPrimary?.childCategories ?? undefined}
          selectedItem={selectedItem}
          handleSecondaryClick={handleSecondaryClick}
          locale={locale}
          isCountrySelectorOpen={isCountrySelectorOpen}
        />
      )}

      {/* TERTIARY COLUMN */}
      {selectedItem.secondaryIndex !== -1 && (
        <TertiaryColumn groups={groupedCategories} locale={locale} />
      )}
    </div>
  );
};

export default memoize(WORHMenuContentV2);
